import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { validateEmail, sanitizeText } from "../utils/validator";
import { useLoading } from "../contexts/LoadingContext";
import {
  HeartIcon,
  MailIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  LogInIcon,
  UserPlusIcon,
  SearchIcon,
} from "../components/icons";

function UserLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setGlobalLoading(true);
    setGlobalLoadingMessage("Signing in...");

    // Validate inputs
    if (!sanitizeText(email).trim()) {
      setError("Email is required");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    try {
      const response = await apiFetch("/public/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: sanitizeText(email),
          password: sanitizeText(password),
        }),
      });

      // Store public user token
      if (response.token) {
        localStorage.setItem("publicUserToken", response.token);
      }

      navigate("/find-matches");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  return (
    <>
      <div className="flex justify-end ">
        <p className="flex text-sm items-center mt-3">Are you Bureau Owner?</p>
        <button
          type="button"
          onClick={() => navigate("/bureau")}
          className="mt-3 px-2 mx-2 rounded-lg border border-[var(--color-border)] py-2 text-sm font-semibold text-[var(--color-text)] transition hover:bg-[var(--color-surface-hover)]"
        >
          Bureau Login
        </button>
      </div>
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-background)]">
        <div className="w-full max-w-md rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-xl shadow-[var(--color-primary-soft)]">
          {/* Brand header */}
          <div className="mb-6 flex flex-col items-center text-center">
            <span className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
              <HeartIcon className="h-7 w-7" />
            </span>
            <h2 className="text-2xl font-bold text-[var(--color-text-strong)]">
              Profile Seeker Login
            </h2>
            <p className="mt-1 text-sm text-[var(--color-text-muted)]">
              Login to save profiles and access advanced search
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-4 py-2 text-[var(--color-danger-text)]">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
                Email
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-[var(--color-text-muted)]">
                  <MailIcon className="h-4 w-4" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-11 pr-3 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password with toggle */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
                Password
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-[var(--color-text-muted)]">
                  <LockIcon className="h-4 w-4" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-11 pr-10 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] transition"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] shadow-sm transition hover:bg-[var(--color-primary-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-soft)] disabled:opacity-50"
            >
              <LogInIcon className="h-4 w-4" />
              {loading ? "Logging in..." : "Login"}
            </button>

            <p className="mt-3 text-center text-sm">
              <button
                type="button"
                onClick={() => navigate("/forgot-password?type=seeker")}
                className="font-medium text-[var(--color-text-muted)] underline-offset-2 hover:underline hover:text-[var(--color-text-strong)]"
              >
                Forgot password?
              </button>
            </p>
          </form>

          <div className="mt-6 border-t border-[var(--color-border)] pt-4">
            <button
              type="button"
              onClick={() => navigate("/public-profiles")}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] py-2 text-sm font-semibold text-[var(--color-text)] transition hover:bg-[var(--color-surface-hover)]"
            >
              <SearchIcon className="h-4 w-4" />
              Browse Profiles (No Login Required)
            </button>
          </div>

          <div className="mt-4 text-center">
            <p className="flex items-center justify-center gap-1 text-sm text-[var(--color-text-muted)]">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="inline-flex items-center gap-1 font-semibold text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] transition"
              >
                <UserPlusIcon className="h-4 w-4" />
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserLogin;
